import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle, AlertCircle, Info, X, FileText } from 'lucide-react';
import { uploadTemplate, uploadChangeList, getAllTemplateManagementData } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';

const TemplateManagement = () => {
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('error');
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('CHANGE_VERIFICATION_ASSESSMENT');
  const [selectedCVAItem, setSelectedCVAItem] = useState(null);
  const [selectedCertificationItem, setSelectedCertificationItem] = useState(null);

  // Tab configuration
  const tabs = [
    { id: 'CHANGE_VERIFICATION_ASSESSMENT', label: 'Change Verification Assessment' },
    { id: 'CERTIFICATION', label: 'Certification' },
    { id: 'VENDOR_CAPABILITY', label: 'Vendor Capability' }
  ];

  const changeverificationassessment = ['Hardware'];

  // Updated categories
  const categories = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
    { id: 'PD_RGB LED', label: 'PD_RGB LED' },
    { id: 'PD_AIO', label: 'PD_AIO' },
    { id: 'PD_EPD', label: 'PD_EPD' },
    { id: 'AVA_Audio', label: 'AVA_Audio' },
    { id: 'AVA_Headphone', label: 'AVA_Headphone' }
  ];

  // Certification sub-items
  const certificationItems = [
    'TV Daily Report Project Management',
    'TV Daily Report UI Layout Management',
    'MNT Control Table',
    'AVA Control Table'
  ];

  const VendorCapability = ['Hardware', 'Software', 'Certification'];

  const vendorcategories = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD', label: 'PD' },
    { id: 'AVA', label: 'AVA' },
    { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
    { id: 'PD_RGB LED', label: 'PD_RGB LED' },
    { id: 'PD_AIO', label: 'PD_AIO' },
    { id: 'PD_EPD', label: 'PD_EPD' },
    { id: 'AVA_Audio', label: 'AVA_Audio' },
    { id: 'AVA_Headphone', label: 'AVA_Headphone' }
  ];

  const selectedType = selectedCVAItem?.toLowerCase();
  const isHardware = selectedType === 'hardware';
  const isSoftware = selectedType === 'software';
  const isCertification = selectedType === 'certification';

  const vendorTitle = isHardware
    ? 'Hardware Change Verification Assessment Template'
    : isSoftware
      ? 'Software Vendor Capability Template'
      : 'Certification Vendor Capability Template';

  const assessmentTitle = isHardware
    ? 'HW Change Assessment Reference Table'
    : 'SW Change Assessment Reference Table';

  const getCertificationTitle = (item) => {
    if (item === 'MNT Control Table' || item === 'AVA Control Table') {
      return `${item} Template`;
    }
    return item;
  };

  // ─── Data Loading ───────────────────────────────────────────────────────────

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllTemplateManagementData();
      if (response.success) {
        setTemplateData(response.data);
        console.log('✓ Template management data loaded successfully');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setUploadError('Failed to load template data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (isMounted) await loadAllData();
    };
    fetchData();
    return () => { isMounted = false; };
  }, [loadAllData]);

  useEffect(() => {
    if (activeTab === 'CHANGE_VERIFICATION_ASSESSMENT') {
      if (!selectedCVAItem) setSelectedCVAItem('Hardware');
    } else if (activeTab === 'CERTIFICATION') {
      if (!selectedCertificationItem) setSelectedCertificationItem(certificationItems[0]);
    } else if (activeTab === 'VENDOR_CAPABILITY') {
      if (!selectedCVAItem) setSelectedCVAItem('Hardware');
    }
  }, [activeTab]);

  // ─── Data Helpers ────────────────────────────────────────────────────────────

  // For Change Verification Assessment and Vendor Capability tabs
  const getDataForCategory = (productTypeId) => {
    const categoryMap = {
      CHANGE_VERIFICATION_ASSESSMENT: 'Change Verification Assessment',
      VENDOR_CAPABILITY: 'Vendor Capability'
    };
    const category = categoryMap[activeTab];
    const sub_category = selectedCVAItem;

    const found = templateData.find(item =>
      item.category === category &&
      item.sub_category?.toLowerCase() === sub_category?.toLowerCase() &&
      item.productType === productTypeId
    );

    return found || {
      category,
      sub_category,
      productType: productTypeId,
      referenceTable: null,
      changeList: null
    };
  };

  // For Certification tab — productType is null, matched by sub_category
  const getDataForCertification = (subCatId) => {
    const found = templateData.find(item =>
      item.category === 'Certification' &&
      item.sub_category?.toLowerCase() === subCatId?.toLowerCase() &&
      item.productType === null
    );
    return found || {
      category: 'Certification',
      sub_category: subCatId,
      productType: null,
      referenceTable: null,
      changeList: null
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // ─── Upload Handlers ─────────────────────────────────────────────────────────

  const handleFileUpload = async (event, productTypeId) => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setModalType('error');
      setModalMessage('Please upload an Excel file (.xlsx, .xls, or .xlsm)');
      setShowModal(true);
      event.target.value = '';
      return;
    }
    const isCertTab = activeTab === 'CERTIFICATION';

    try {
      const uploadKey = isCertTab ? selectedCertificationItem : productTypeId;
      setUploadingCategory(uploadKey);
      setUploadingType('reference');
      setUploadError('');
      setUploadMessage('');

      const decoded = decodedToken();
      const email = decoded?.email || '';

      if (!email) {
        setModalType('error');
        setModalMessage('Unable to retrieve user email. Please log in again.');
        setShowModal(true);
        event.target.value = '';
        setUploadingCategory(null);
        setUploadingType(null);
        return;
      }

      const categoryMap = {
        CHANGE_VERIFICATION_ASSESSMENT: 'Change Verification Assessment',
        CERTIFICATION: 'Certification',
        VENDOR_CAPABILITY: 'Vendor Capability'
      };
      const category = categoryMap[activeTab];

      let sub_category;
      let productType;

      if (isCertTab) {
        sub_category = selectedCertificationItem;
        productType = null;
      } else {
        sub_category = selectedCVAItem;
        productType = productTypeId;
      }

      console.log(`Uploading → category: ${category} | sub_category: ${sub_category} | productType: ${productType ?? 'null'} | file: ${file.name}`);

      const response = await uploadTemplate(file, category, sub_category, productType, email);

      if (response.success) {
        if (response.isDuplicate) {
          setModalType('duplicate');
          setDuplicateInfo(response.existingTemplate);
          const label = isCertTab ? sub_category : productTypeId;
          setModalMessage(
            `The uploaded template is identical to the existing ${label} template.\n\n` +
            `No changes detected in:\n` +
            `• Test case mappings\n` +
            `• Test categories\n` +
            `• Change item definitions\n\n` +
            `The existing template will continue to be used.`
          );
          setShowModal(true);
          setUploadMessage(`Template data unchanged. Current version remains active.`);
          setTimeout(() => setUploadMessage(''), 5000);
        } else {
          setModalType('success');
          setModalMessage(`Template uploaded successfully!`);
          setShowModal(true);
          const label = isCertTab ? sub_category : productTypeId;
          setUploadMessage(`Template uploaded successfully for ${label}`);
          setTimeout(() => setUploadMessage(''), 5000);
        }
        await loadAllData();
      } else {
        setModalType('error');
        setModalMessage(response.message || 'Upload failed');
        setShowModal(true);
        setUploadError(response.message || 'Upload failed');
        setTimeout(() => setUploadError(''), 5000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data) {
        if (error.response.data.success === false) {
          errorMessage = error.response.data.message || error.response.data.error;
        } else if (error.response.data.error === 'Invalid template file') {
          errorMessage = error.response.data.message;
          if (error.response.data.availableSheets) {
            errorMessage += `\n\nAvailable sheets in the file:\n${error.response.data.availableSheets.join(', ')}`;
          }
        } else {
          errorMessage = error.response.data.message || error.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setModalType('error');
      setModalMessage(`Failed to upload template:\n\n${errorMessage}`);
      setShowModal(true);
      setUploadError(`Upload failed: ${errorMessage}`);
      setTimeout(() => setUploadError(''), 5000);
    } finally {
      setUploadingCategory(null);
      setUploadingType(null);
      event.target.value = '';
    }
  };

  const handleChangeListUpload = async (event, productType) => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setModalType('error');
      setModalMessage('Please upload an Excel file (.xlsx, .xls, or .xlsm)');
      setShowModal(true);
      event.target.value = '';
      return;
    }

    try {
      setUploadingCategory(productType);
      setUploadingType('changelist');
      setUploadError('');
      setUploadMessage('');

      const decoded = decodedToken();
      const email = decoded?.email || '';
      const userType = decoded?.userType || 'PowerAdmin';

      if (!email) {
        setModalType('error');
        setModalMessage('Unable to retrieve user email. Please log in again.');
        setShowModal(true);
        event.target.value = '';
        setUploadingCategory(null);
        setUploadingType(null);
        return;
      }

      if (!userType) {
        setModalType('error');
        setModalMessage('Unable to retrieve user type. Please log in again.');
        setShowModal(true);
        event.target.value = '';
        setUploadingCategory(null);
        setUploadingType(null);
        return;
      }

      const categoryMap = {
        CHANGE_VERIFICATION_ASSESSMENT: 'Change Verification Assessment',
        CERTIFICATION: 'Certification',
        VENDOR_CAPABILITY: 'Vendor Capability'
      };
      const category = categoryMap[activeTab];
      const sub_category = selectedCVAItem;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('sub_category', sub_category);
      formData.append('productType', productType);

      const response = await uploadChangeList(formData, email, userType);

      if (response.success) {
        if (response.isDuplicate) {
          setModalType('duplicate');
          setDuplicateInfo(response.existingChangeList);
          setModalMessage(
            `The uploaded change list is identical to the existing ${productType} template.\n\n` +
            `No changes detected.\n\n` +
            `The existing template will continue to be used.`
          );
          setShowModal(true);
          setUploadMessage(`Change list unchanged. Current version remains active.`);
          setTimeout(() => setUploadMessage(''), 5000);
        } else {
          setModalType('success');
          setModalMessage(`Change list uploaded successfully!`);
          setShowModal(true);
          setUploadMessage(`Change list uploaded successfully for ${productType}`);
          setTimeout(() => setUploadMessage(''), 5000);
        }
        await loadAllData();
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = error.message || 'Unknown error';
      if (error.response?.data?.error === 'Invalid Excel file') {
        errorMessage = error.response.data.message;
        if (error.response.data.expectedSheets) {
          errorMessage += `\n\nExpected sheets: ${error.response.data.expectedSheets.join(', ')}`;
        }
      }
      setModalType('error');
      setModalMessage(`Failed to upload change list:\n\n${errorMessage}`);
      setShowModal(true);
      setUploadError(`Upload failed: ${errorMessage}`);
      setTimeout(() => setUploadError(''), 5000);
    } finally {
      setUploadingCategory(null);
      setUploadingType(null);
      event.target.value = '';
    }
  };

  // ─── Misc Handlers ───────────────────────────────────────────────────────────

  const handleGoBack = () => navigate('/management');

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
    setDuplicateInfo(null);
  };

  const getModalStyles = () => {
    switch (modalType) {
      case 'duplicate':
        return {
          bgColor: 'bg-yellow-900/30',
          borderColor: 'border-yellow-500',
          iconColor: 'text-yellow-400',
          icon: Info,
          title: 'Duplicate Template Detected'
        };
      case 'success':
        return {
          bgColor: 'bg-green-900/30',
          borderColor: 'border-green-500',
          iconColor: 'text-green-400',
          icon: CheckCircle,
          title: 'Success'
        };
      default:
        return {
          bgColor: 'bg-red-900/30',
          borderColor: 'border-red-500',
          iconColor: 'text-red-400',
          icon: AlertCircle,
          title: 'Upload Error'
        };
    }
  };

  // ─── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E1114] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading templates...</p>
        </div>
      </div>
    );
  }

  const modalStyles = getModalStyles();
  const ModalIcon = modalStyles.icon;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#0E1114] text-white">
      {/* Header */}
      <div className="border-b border-gray-800">
        <div className="px-6 py-4 flex items-center gap-3">
          <ArrowLeft
            className="w-6 h-6 cursor-pointer hover:text-blue-400 transition-colors"
            onClick={handleGoBack}
          />
          <h1 className="text-xl font-semibold">Template Management</h1>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-t border-gray-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-2 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-white bg-gray-900'
                  : 'text-gray-400 hover:text-white hover:bg-gray-900/50'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Status Messages */}
        {uploadMessage && (
          <div className="mb-4 p-3 bg-green-900/50 border border-green-500 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-300 text-sm">{uploadMessage}</p>
          </div>
        )}
        {uploadError && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm">{uploadError}</p>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`${modalStyles.bgColor} border ${modalStyles.borderColor} p-6 rounded-lg max-w-md w-full shadow-2xl`}>
              <div className="flex items-center gap-3 mb-4">
                <ModalIcon className={`w-8 h-8 ${modalStyles.iconColor} flex-shrink-0`} />
                <h3 className="text-lg font-semibold text-white">{modalStyles.title}</h3>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-300 whitespace-pre-line leading-relaxed">{modalMessage}</p>
                {duplicateInfo && (
                  <div className="mt-4 p-3 bg-gray-800/50 rounded border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">
                      <span className="font-semibold">Filename:</span> {duplicateInfo.filename}
                    </p>
                    <p className="text-xs text-gray-400">
                      <span className="font-semibold">Last Updated:</span> {formatDate(duplicateInfo.uploadDate)}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  className={`px-6 py-2 rounded-md font-medium transition-all ${
                    modalType === 'duplicate' ? 'bg-yellow-600 hover:bg-yellow-500' :
                    modalType === 'success'   ? 'bg-green-600 hover:bg-green-500'   :
                                                'bg-blue-600 hover:bg-blue-500'
                  } text-white active:scale-95`}
                  onClick={closeModal}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: CHANGE VERIFICATION ASSESSMENT
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'CHANGE_VERIFICATION_ASSESSMENT' && (
          <div className="sm:mt-6 mt-1 space-y-6">
            {/* TOP MENU */}
            <div className="w-full max-w-xl">
              <div className="bg-[#1a1d23] rounded-lg border border-gray-800 divide-y divide-gray-800">
                {changeverificationassessment.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedCVAItem(prev => (prev === item ? null : item))}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      selectedCVAItem === item ? 'bg-gray-600 text-white' : 'hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      <span className="text-base">{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* DETAILS — only Hardware for CVA */}
            {isHardware && (
              <div className="space-y-6 w-full">
                <div className="px-4 sm:px-6 md:px-10">
                  <h2 className="text-base sm:text-2xl md:text-3xl font-semibold text-white mb-4">
                    Hardware Change Verification Assessment Template
                  </h2>
                  <div className="p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm md:text-base text-yellow-200 leading-relaxed">
                      <span className="font-semibold">Attention:</span> Before update, please first rename
                      the Excel file as per the defined naming convention.
                    </p>
                  </div>
                </div>

                {/* CATEGORY LOOP */}
                {categories.map((category) => {
                  const data = getDataForCategory(category.id);
                  const template = data.referenceTable;
                  const changeList = data.changeList;
                  const isUploadingReference =
                    uploadingCategory === category.id && uploadingType === 'reference';
                  const isUploadingChangeList =
                    uploadingCategory === category.id && uploadingType === 'changelist';

                  return (
                    <div key={category.id} className="space-y-4 w-full">
                      <h2 className="text-lg font-semibold text-blue-500">{category.label}</h2>

                      {/* Reference Table */}
                      <h3 className="text-sm font-medium text-gray-300">HW Change Assessment Reference Table</h3>
                      <div className="bg-[#1a1d23] rounded-lg p-4 border border-gray-800 hover:border-gray-700 transition-colors w-full">
                        {template ? (
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-white mb-2">{template.filename}</p>
                              <p className="text-xs text-gray-400 mb-1">Uploaded by: {template.uploadedBy}</p>
                              <p className="text-xs text-gray-500">
                                Last updated: {formatDate(template.lastModified || template.uploadDate)}
                              </p>
                              {template.fileVersion && (
                                <p className="text-xs text-gray-500 font-mono mt-1">Version: {template.fileVersion}</p>
                              )}
                            </div>
                            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 ml-4" />
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No template uploaded for {category.label}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <label className={`bg-gray-700 hover:bg-gray-600 text-sm text-white px-5 py-2.5 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${
                          isUploadingReference ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                        }`}>
                          {isUploadingReference ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4" />Update Template</>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept=".xlsx,.xls,.xlsm"
                            onChange={(e) => handleFileUpload(e, category.id)}
                            disabled={isUploadingReference}
                          />
                        </label>
                      </div>

                      {/* Change Item List */}
                      <h3 className="text-sm font-medium text-gray-300">HW Change Item List</h3>
                      <div className="bg-[#1a1d23] rounded-lg p-4 border border-gray-800 hover:border-gray-700 transition-colors w-full">
                        {changeList ? (
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-white mb-2">{changeList.filename}</p>
                              <p className="text-xs text-gray-400 mb-1">Uploaded by: {changeList.uploadedBy}</p>
                              <p className="text-xs text-gray-500">
                                Last updated: {formatDate(changeList.uploadDate)}
                              </p>
                            </div>
                            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 ml-4" />
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No change list uploaded for {category.label}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <label className={`bg-gray-700 hover:bg-gray-600 text-sm text-white px-5 py-2.5 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${
                          isUploadingChangeList ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                        }`}>
                          {isUploadingChangeList ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4" />Update Change List</>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept=".xlsx,.xls,.xlsm"
                            onChange={(e) => handleChangeListUpload(e, category.id)}
                            disabled={isUploadingChangeList}
                          />
                        </label>
                      </div>

                      <div className="border-t border-gray-800 my-6"></div>
                    </div>
                  );
                })}

                {/* Upload Guidelines */}
                <div className="mt-8 p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg w-full">
                  <h3 className="text-sm font-semibold text-blue-400 mb-3">Template Upload Guidelines</h3>
                  <ul className="text-xs text-gray-400 space-y-1.5">
                    <li>• Upload Excel files (.xlsx, .xls, .xlsm)</li>
                    <li>• Follow the predefined template format</li>
                    <li>• New uploads replace the existing template</li>
                    <li>• Supported product types: {categories.map(c => c.label).join(', ')}</li>
                  </ul>
                </div>

                {/* Template Status */}
                <div className="mt-6 p-4 bg-gray-900/30 border border-gray-700/30 rounded-lg w-full">
                  <h3 className="text-sm font-semibold text-gray-300 mb-4">Template Status</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {categories.map((category) => {
                      const data = getDataForCategory(category.id);
                      const template = data.referenceTable;
                      return (
                        <div key={category.id} className="flex items-center gap-2">
                          <div className={`text-lg ${template ? 'text-green-400' : 'text-gray-300'}`}>
                            {template ? '✓' : '○'}
                          </div>
                          <div>
                            <div className="text-xs text-white font-semibold">{category.label}</div>
                            {template?.fileVersion && (
                              <div className="text-xs text-gray-500 font-mono">{template.fileVersion}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: CERTIFICATION
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'CERTIFICATION' && (
          <div className="sm:mt-6 mt-1 space-y-6">
            <div className="flex justify-center">
              <div className="w-full max-w-xl">
                <div className="bg-[#1a1d23] rounded-lg border border-gray-800 divide-y divide-gray-800">
                  {certificationItems.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedCertificationItem(prev => (prev === item ? null : item))}
                      className={`px-4 py-3 cursor-pointer transition-colors ${
                        selectedCertificationItem === item ? 'bg-gray-600 text-white' : 'hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        <span className="sm:text-base text-sm">{item}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* DETAILS FOR SELECTED CERTIFICATION ITEM */}
            {selectedCertificationItem && (() => {
              const certData = getDataForCertification(selectedCertificationItem);
              const template = certData.referenceTable;
              const isUploadingReference =
                uploadingCategory === selectedCertificationItem && uploadingType === 'reference';

              return (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h2 className="sm:text-3xl text-base font-semibold text-white">
                      {getCertificationTitle(selectedCertificationItem)}
                    </h2>

                    <div className="mb-3 p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs sm:text-sm md:text-base text-yellow-200 leading-relaxed">
                        <span className="font-semibold">Attention:</span> Before update, please first rename the Excel file as: WKXXXX {selectedCertificationItem} Cert Tracking.xlsx
                      </p>
                    </div>

                    {/* Template Card */}
                    <div className="bg-[#1a1d23] rounded-lg p-4 mb-3 border border-gray-800 hover:border-gray-700 transition-colors">
                      {template ? (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-white mb-2">{template.filename}</p>
                            <p className="text-xs text-gray-400 mb-1">Uploaded by: {template.uploadedBy}</p>
                            <p className="text-xs text-gray-500">
                              Last updated: {formatDate(template.lastModified || template.uploadDate)}
                            </p>
                            {template.fileVersion && (
                              <p className="text-xs text-gray-500 font-mono mt-1">Version: {template.fileVersion}</p>
                            )}
                          </div>
                          <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 ml-4" />
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No template uploaded for {selectedCertificationItem}</p>
                        </div>
                      )}
                    </div>

                    {/* Upload Button */}
                    <div className="flex justify-end">
                      <label className={`bg-gray-700 hover:bg-gray-600 text-sm text-white px-5 py-2.5 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${
                        isUploadingReference ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                      }`}>
                        {isUploadingReference ? (
                          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Uploading...</>
                        ) : (
                          <><Upload className="w-4 h-4" />Update Template</>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept=".xlsx,.xls,.xlsm"
                          onChange={(e) => handleFileUpload(e, selectedCertificationItem)}
                          disabled={isUploadingReference}
                        />
                      </label>
                    </div>

                    <div className="border-t border-gray-800 my-6"></div>
                  </div>

                  {/* Upload Guidelines */}
                  <div className="mt-8 p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg">
                    <h3 className="text-base font-semibold text-blue-400 mb-3">Template Upload Guidelines</h3>
                    <ul className="text-sm text-white space-y-1.5 leading-relaxed">
                      <li>• Upload Excel files (.xlsx, .xls, or .xlsm)</li>
                      <li>• Each update file should use the predefined template format</li>
                      <li>• Uploading a new file will replace the existing one</li>
                      <li>• Supported product type: {selectedCertificationItem}</li>
                    </ul>
                  </div>

                  {/* Template Status */}
                  <div className="mt-6 p-4 bg-gray-900/30 border border-gray-700/30 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-300 mb-3">Template Status</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      <div className="text-left">
                        <div className="flex items-center justify-left gap-1.5">
                          <div className={`text-lg ${template ? 'text-green-400' : 'text-gray-300'} mt-1`}>
                            {template ? '✓' : '○'}
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-white font-semibold whitespace-nowrap">
                              {selectedCertificationItem}
                            </div>
                            {template?.fileVersion && (
                              <div className="text-xs text-gray-500 font-mono mt-0">{template.fileVersion}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB: VENDOR CAPABILITY
        ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'VENDOR_CAPABILITY' && (
          <div className="sm:mt-6 mt-1 space-y-6">
            <div className="flex justify-end">
              <div className="w-full sm:w-90 md:w-96 lg:w-[420px] bg-[#1a1d23] border border-gray-800 divide-y divide-gray-800 rounded-lg overflow-hidden">
                {VendorCapability.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedCVAItem(prev => (prev === item ? null : item))}
                    className={`w-92 px-4 py-3 cursor-pointer bg-[#1a1d23] border border-gray-800 transition-colors ${
                      selectedCVAItem === item ? 'bg-gray-600 text-white' : 'hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      <span className="sm:text-base text-sm">{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {(isHardware || isSoftware || isCertification) && (
              <div className="mt-6 w-full space-y-6">
                {/* Static Header */}
                <div className="ml-4">
                  <h2 className="sm:text-3xl text-base font-semibold text-white mb-2 ml-3">
                    {vendorTitle}
                  </h2>
                  <div className="p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm md:text-base text-yellow-200 leading-relaxed">
                      <span className="font-semibold">Attention:</span> Before update, please
                      first rename the Excel file as: Product type Cert Verification
                      Assessment_version.xlsm
                    </p>
                  </div>
                </div>

                {/* Category Loop */}
                {vendorcategories.map((category) => {
                  // Hardware & Software: hide main PD and AVA
                  if ((isHardware || isSoftware) && (category.id === 'PD' || category.id === 'AVA')) {
                    return null;
                  }
                  // Certification: only show TV, MNT, PD, AVA
                  if (isCertification && !['TV', 'MNT', 'PD', 'AVA'].includes(category.id)) {
                    return null;
                  }

                  const data = getDataForCategory(category.id);
                  const template = data.referenceTable;
                  const isUploadingReference =
                    uploadingCategory === category.id && uploadingType === 'reference';

                  return (
                    <div key={category.id} className="space-y-2 w-full">
                      <h2 className="text-lg font-semibold text-blue-500 flex items-center gap-2">
                        {isCertification && ['TV', 'MNT', 'PD', 'AVA'].includes(category.id) && (
                          <FileText className="w-5 h-5 text-blue-400" />
                        )}
                        {category.label}
                      </h2>

                      <h3 className="text-base font-medium mb-1 text-green-300">
                        {isHardware
                          ? 'HW Capability Map Reference Table'
                          : isSoftware
                            ? 'SW Capability Map Reference Table'
                            : ''}
                      </h3>

                      {/* Template Card */}
                      <div className="bg-[#1a1d23] rounded-lg p-4 mb-3 border border-gray-800 hover:border-gray-700 transition-colors w-full">
                        {template ? (
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-white mb-2">{template.filename}</p>
                              <p className="text-xs text-gray-400 mb-1">Uploaded by: {template.uploadedBy}</p>
                              <p className="text-xs text-gray-500">
                                Last updated: {formatDate(template.lastModified || template.uploadDate)}
                              </p>
                              {template.fileVersion && (
                                <p className="text-xs text-gray-500 font-mono mt-1">Version: {template.fileVersion}</p>
                              )}
                            </div>
                            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 ml-4" />
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <AlertCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No template uploaded for {category.label}</p>
                          </div>
                        )}
                      </div>

                      {/* Upload Button */}
                      <div className="flex justify-end">
                        <label className={`bg-gray-700 hover:bg-gray-600 text-sm text-white px-5 py-2.5 rounded-md transition-all cursor-pointer inline-flex items-center gap-2 ${
                          isUploadingReference ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                        }`}>
                          {isUploadingReference ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4" />Update Template</>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept=".xlsx,.xls,.xlsm"
                            onChange={(e) => handleFileUpload(e, category.id)}
                            disabled={isUploadingReference}
                          />
                        </label>
                      </div>

                      <div className="border-t border-gray-800 my-6" />
                    </div>
                  );
                })}

                {/* Upload Guidelines */}
                <div className="mt-8 p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg">
                  <h3 className="text-base font-semibold text-blue-400 mb-3">Template Upload Guidelines</h3>
                  <ul className="text-sm text-white space-y-1.5 leading-relaxed">
                    <li>• Upload Excel files (.xlsx, .xls, or .xlsm)</li>
                    <li>• Each update file must follow the predefined capability format</li>
                    <li>• Uploading a new file will replace the existing one</li>
                    <li>• Supported category: {isHardware ? 'Hardware' : isSoftware ? 'Software' : 'Certification'}</li>
                  </ul>
                </div>

                {/* Template Status */}
                <div className="mt-6 p-4 bg-gray-900/30 border border-gray-700/30 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">Template Status</h3>
                  <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {vendorcategories.map((category) => {
                      if ((isHardware || isSoftware) && (category.id === 'PD' || category.id === 'AVA')) return null;
                      if (isCertification && !['TV', 'MNT', 'PD', 'AVA'].includes(category.id)) return null;

                      const data = getDataForCategory(category.id);
                      const template = data.referenceTable;

                      return (
                        <div key={category.id} className="flex items-center gap-2">
                          <div className={`text-lg ${template ? 'text-green-400' : 'text-gray-300'}`}>
                            {template ? '✓' : '○'}
                          </div>
                          <div>
                            <div className="text-xs text-white font-semibold">{category.label}</div>
                            {template?.fileVersion && (
                              <div className="text-xs text-gray-500 font-mono">{template.fileVersion}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateManagement;

